import React, { FC } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import { Logo } from "@components/ui/Logo";
import { Image } from "@components/ui/Image";
import Preferences from "../Navbar/components/Preferences";
import { getAbsoluteUrl } from "@helpers/getAbsoluteUrl";
import { ROUTES } from "@root/routes";
import { useLocales } from "@helpers/hooks/useLocales";

interface LinkItem {
  name: string;
  href: string;
  target?: string;
}

interface LinksColumnProps {
  title: string;
  links?: LinkItem[];
}

const LinksColumn: FC<LinksColumnProps> = ({ title, links }) => {
  return (
    <div className="mb-10 md:mb-0">
      <h3 className="font-medium text-lg mb-6 border-b md:border-0 border-gray-300 pb-2">
        {title}
      </h3>
      <ul>
        {links &&
          links.map((item) => (
            <li key={item.name} className="mb-2">
              <Link
                href={item.href}
                className="hover:underline text-gray-600"
                target={item.target || "_self"}
              >
                {item.name}
              </Link>
            </li>
          ))}
      </ul>
    </div>
  );
};

const Footer = () => {
  const router = useRouter();
  const { locale } = useLocales();

  const spaceClasses = {
    "/supplier/[id]": "mb-20 md:mb-0",
    "/supplier/[id]/preview": "mb-20 md:mb-0",
    "/[country]/tour/[id]": "mb-24 md:mb-0",
    "/blog/[slug]": "mb-20 md:mb-0",
    "/blog/[slug]/preview": "mb-20 md:mb-0",
    "/[country]/tour/[id]/preview": "mb-24 md:mb-0",
    "/airport-transfers/search": "mb-24 lg:mb-0",
  } as { [key: string]: string };

  const addSpace = spaceClasses[router.pathname] ?? "";

  const spaceClassesSize = {
    "/blog/[slug]": "mb-0 md:mb-16",
    "/blog/[slug]/preview": "mb-0 md:mb-16",
  } as { [key: string]: string };

  const addSpaceScreen = spaceClassesSize[router.pathname] ?? "";

  const gowithguide = [
    {
      name: "About us",
      href: getAbsoluteUrl(ROUTES.about(), locale),
    },
    {
      name: "Booking Process",
      href: getAbsoluteUrl(ROUTES.bookingProcess(), locale),
    },
    {
      name: "Loyalty Program",
      href: getAbsoluteUrl(ROUTES.loyalty(), locale),
    },
    {
      name: "Blog",
      href: getAbsoluteUrl(ROUTES.blog.index()),
    },
  ];

  const support = [
    {
      name: "Contact Us",
      href: getAbsoluteUrl(ROUTES.contact()),
    },
    {
      name: "FAQ",
      href: getAbsoluteUrl(ROUTES.faq(), locale),
    },
    {
      name: "Terms & Conditions",
      href: getAbsoluteUrl(ROUTES.terms(), locale),
    },
    {
      name: "Privacy Policy",
      href: getAbsoluteUrl(ROUTES.privacy(), locale),
    },
    {
      name: "Cancellation Policy",
      href: getAbsoluteUrl(ROUTES.cancellationPolicy(), locale),
    },
  ];

  const followUs = [
    {
      name: "Instagram",
      href: "https://www.instagram.com/gowithguide",
      target: "_blank",
    },
    {
      name: "Facebook",
      href: "https://www.facebook.com/gowithguide.tours",
      target: "_blank",
    },
  ];

  const workingWithUs = [
    {
      name: "Tour Guide",
      href: getAbsoluteUrl(ROUTES.supplierApply.index()),
    },
    {
      name: "Careers",
      href: "https://travelience.jp/jobs/",
      target: "_blank",
    },
    {
      name: "Affiliates",
      href: getAbsoluteUrl(ROUTES.contact()),
    },
  ];

  // Hide the footer: Check if the current pathname matches the patterns
  const hideFooter =
    router.pathname.startsWith("/auth/bookings/") &&
    (router.pathname.includes("/recommended") ||
      router.pathname.includes("/thanks"));

  if (hideFooter) {
    return null;
  }

  const isFaqPage =
    router.pathname.startsWith("/faq") || router.pathname.startsWith("/ja/faq");

  const loyaltyFooterPadding =
    router.pathname === "/loyalty-program" ? "pb-[96px] md:pb-8" : "pb-8";

  return (
    <footer
      className={`border-t border-gray-200 bg-gray-100 pt-8 ${loyaltyFooterPadding}`}
    >
      <div className="max-w-6xl mx-auto print:hidden">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-4 px-8">
          <LinksColumn title="GoWithGuide" links={gowithguide} />
          <LinksColumn title="Support" links={support} />
          <LinksColumn title="Follow Us" links={followUs} />
          <LinksColumn title="Work With Us" links={workingWithUs} />
        </div>
        <div className="flex justify-end lg:justify-end md:justify-center max-md:justify-center mr-4">
          {/* used img instead of Image of next js because we need width and height for seo */}
          <Image
            src="/img/cc-badges-ppmcvdam-optimized.avif"
            alt="Buy now with PayPal & credit card"
            fallback="/img/cc-badges-ppmcvdam.png"
            activeCache={true}
            width={316}
            height={40}
          />
        </div>
        <div
          className={`flex flex-wrap md:flex-nowrap items-center justify-between border-t border-gray-200 p-4 ${addSpace} ${isFaqPage ? "pb-14 lg:pb-0" : "pb-0"} mt-6 ${addSpaceScreen}`}
        >
          <div className="flex items-center mx-auto md:mx-0">
            <Logo full={true} className="mr-4" />{" "}
            <span className="text-gray-600">© {new Date().getFullYear()}</span>
          </div>
          <div className="flex items-center mt-4 md:mt-0 w-full md:w-auto">
            <div className="border border-gray-300 hover:border-gray-400 p-2 w-full md:w-auto rounded px-4 text-center">
              <Preferences />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
